import React from 'react';
import { common, query } from '@kurtosys/ksys-app-template';

import BarScale from '../../charting/BarScale/BarScale.js';
import { ChangeIndicator } from '../../overview/ChangeIndicator/ChangeIndicator.js';
import Actions from '../Actions/Actions.js';
import Checkbox from '../Checkbox/Checkbox.js';
import Color from '../Color/Color.js';
import ContextMenu from '../ContextMenu/ContextMenu.js';
import Icon from '../Icon/Icon.js';
import Link from '../Link/Link.js';
import { ILinkProps } from '../Link/models/index.js';
import MorningStar from '../MorningStar/MorningStar.js';
import Scale from '../Scale/Scale.js';
import ScaleLegend from '../ScaleLegend/ScaleLegend.js';
import ToggleButton from '../ToggleButton/ToggleButton.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate from '../Translate/Translate.js';
import { IWeightingBarProps } from '../WeightingBar/models/index.js';
import { WeightingBar } from '../WeightingBar/WeightingBar.js';

import DocumentContextMenu from './components/DocumentContextMenu/DocumentContextMenu.js';
import { Row } from './components/Row/Row.js';
import { ITableColumnHeaderDisplay, TableColumnDisplayType, TableColumnHeaderDisplayType } from './models/index.js';
import { ITableColumn } from './models/ITableColumn.js';
import { ITableColumnBarScaleOptions } from './models/ITableColumnBarScaleOptions.js';
import { ITableGroup } from './models/ITableGroup.js';
import { ITableGroupByOptions } from './models/ITableGroupByOptions.js';
import { ITableRow } from './models/ITableRow.js';
import { Table } from './Table.js';
import { TableLogicBase } from './TableLogicBase.js';

const isNullOrUndefined = common.commonUtils.isNullOrUndefined;
export class TableLogic extends TableLogicBase {
	getValue(
		row: ITableRow,
		column: ITableColumn,
		translationContext?: ITranslationContextValue,
		rowComponent?: Row,
		table?: Table<any>,
		cellDescribedby?: string,
		rowIndex?: number,
		grouping?: ITableGroup,
	) {
		const value = this.getRawValue(row, column);
		let response = this.formatValueForColumn(value, column, translationContext);
		const display = this.getColumnDisplay(column);
		const { type, options = {} } = display;
		const superscripts = this.getCellSuperscripts(column, row);
		if (type === TableColumnDisplayType.contextMenu && options.contextMenu) {
			response = <ContextMenu {...options.contextMenu} payload={row} />;
		} else if (type === TableColumnDisplayType.documentContextMenu && options.documentContextMenu) {
			response = <DocumentContextMenu {...options.documentContextMenu} payload={row} />;
		} else if (type === TableColumnDisplayType.actions && options.actions) {
			options.actions.buttons.forEach((button) => {
				button.payload = row;
			});
			response = <Actions {...options.actions} />;
		} else if (type === TableColumnDisplayType.checkbox && options.checkbox) {
			const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
				const value = event.target.checked;
				if (options.checkbox && options.checkbox.onChange) {
					options.checkbox.onChange(row, value);
				}
				if (column.key === '_selected' && table && table.rowSelectionChange) {
					table.rowSelectionChange(value, row);
				}
			};
			response = <Checkbox value={!!value} {...options.checkbox} onChange={onChange} />;
		} else if (type === TableColumnDisplayType.icon && options.icon) {
			response = <Icon {...options.icon} />;
		} else if (type === TableColumnDisplayType.morningstarRating && options.morningstarRating) {
			response = <MorningStar {...options.morningstarRating} morningStarRating={parseInt(value, 10)} />;
		} else if (type === TableColumnDisplayType.color) {
			response = <Color {...(options.color || {})} color={value} />;
		} else if (type === TableColumnDisplayType.scale && options.scale) {
			response = <Scale {...(options.scale || {})} value={parseInt(value, 10)} />;
		} else if (type === TableColumnDisplayType.barScale && options.barScale) {
			const Element = this.getBarScaleValue(row, rowIndex, options.barScale);
			if (Element) {
				response = Element;
			}
		} else if (type === TableColumnDisplayType.expander && options.expander) {
			if (grouping && grouping.id === '_lastGroup') {
				return null;
			}
			if (
				grouping &&
				grouping.disableToggle &&
				grouping.disableToggleConditional &&
				grouping.disableToggle(row, grouping.disableToggleConditional)
			) {
				row._toggleDisabled = true;
			}
			if (
				grouping &&
				grouping.hideToggle &&
				grouping.hideToggleConditional &&
				grouping.hideToggle(row, grouping.hideToggleConditional)
			) {
				return null;
			}
			response = (
				<ToggleButton
					{...options.expander}
					onChange={async (active: boolean) => {
						if (!row._toggleDisabled) {
							row._expanded = active;
							if (rowComponent) {
								rowComponent.toggleExpanded(active);
								if (active) {
									rowComponent.onExpand(row);
								} else {
									rowComponent.onCollapse(row);
								}
							}
						}
					}}
					active={row._expanded}
					disabled={row._toggleDisabled}
					loading={rowComponent && rowComponent.fetchingChildren}
					buttonProps={{
						describedby: cellDescribedby,
					}}
					iconActive={{
						asset: grouping?.expandButtonAssets?.activeAsset
							? grouping.expandButtonAssets.activeAsset
							: 'base.toggleButton.active',
						accessibilityText: 'Collapse',
					}}
					iconInactive={{
						asset: grouping?.expandButtonAssets?.inactiveAsset
							? grouping.expandButtonAssets.inactiveAsset
							: 'base.toggleButton.inactive',
						accessibilityText: 'Expand',
					}}
					iconDisabledInactive={{
						asset: grouping?.expandButtonAssets?.disabledAsset
							? grouping.expandButtonAssets.disabledAsset
							: 'base.toggleButton.disabledInactive',
						accessibilityText: 'Disabled Expand',
					}}
					suppressAnalytics={true}
				/>
			);
		} else if (type === TableColumnDisplayType.link && options.link) {
			const { redirect, conditional } = options.link;
			const props: ILinkProps = {};
			if (redirect) {
				let allowLink = true;
				if (conditional) {
					const conditionalHelper = new common.helpers.ConditionalHelper(
						{ queryClass: query.Query },
						conditional,
						this.translationHelper,
					);
					allowLink = conditionalHelper.matches({
						row,
						column,
						value,
					});
				}
				if (allowLink) {
					if (redirect) {
						const redirectHelper = new common.helpers.RedirectHelper(
							{ queryClass: query.Query, transformClass: query.transform.Transform },
							window.location.href,
						);
						const inputs = {
							...this.props.appParamsInputs,
							row,
							column,
							value,
						};
						const redirectPath = redirectHelper.getRedirectPath(redirect, inputs);
						props.href = redirectPath;
					}

					response = (
						<Link {...props}>
							<Translate>{response}</Translate>
						</Link>
					);
				}
			}
			if (isNullOrUndefined(response)) {
				response = column.placeholder || this.props.placeholder;
			}
		} else if (type === TableColumnDisplayType.weightingBar && options.weightingBar) {
			const weightingBlocksConfig = options.weightingBar.weightingBlocks;
			const weightingBarProps: IWeightingBarProps = {
				weightingBlocks: (value as string[]).map((val, idx) => {
					const weightingBlock = weightingBlocksConfig[idx];
					const block = { value: val };
					if (weightingBlock) {
						Object.assign(block, weightingBlock);
					}
					return block;
				}),
			};
			response = <WeightingBar {...weightingBarProps} />;
		} else {
			if (isNullOrUndefined(response)) {
				response = column.placeholder || this.props.placeholder;
			} else if (type === TableColumnDisplayType.changeIndicator && options.changeIndicator) {
				const changeIndicatorProps = Object.assign({}, options.changeIndicator, {
					superscripts,
					value: response,
				});
				const { directionValue } = changeIndicatorProps;
				if (directionValue && !common.commonUtils.isNumber(parseInt(directionValue, 10))) {
					changeIndicatorProps.directionValue = this.getRawValue(row, { id: '', key: directionValue });
				}
				response = <ChangeIndicator {...changeIndicatorProps} />;
			}
		}

		return response;
	}

	getBarScaleValue(row: object, rowIndex?: number, options?: ITableColumnBarScaleOptions) {
		if (this.BarScaleCalculator && typeof rowIndex === 'number' && options) {
			let optionsOverrides;
			if (options.rowConditionals) {
				const rowConditional = options.rowConditionals.find((item) => {
					const conditional = new common.helpers.ConditionalHelper(
						{ queryClass: query.Query },
						item.conditional,
					);
					return conditional.matchesWithOptions({ instance: row });
				});
				if (rowConditional) {
					optionsOverrides = rowConditional.renderOptions;
				}
			}
			const barScaleProps = this.BarScaleCalculator.getPropsForDataIndex(rowIndex, optionsOverrides);
			if (barScaleProps) {
				return <BarScale {...barScaleProps} />;
			}
		}
		return;
	}

	getHeaderValue(headerDisplay: ITableColumnHeaderDisplay) {
		const { type, options = {} } = headerDisplay;
		let response;
		if (type === TableColumnHeaderDisplayType.scaleLegend && options.scaleLegend) {
			response = <ScaleLegend {...options.scaleLegend} />;
		} else if (type === TableColumnHeaderDisplayType.weightingBar && options.weightingBar) {
			response = <WeightingBar {...options.weightingBar} />;
		}
		return response;
	}

	groupRowsBy = <TRow extends ITableRow>(rows: TRow[]): Record<string, TRow[]> => {
		const groupType = this.getGroupType(this.grouping);
		const groupByOptions = this.getGroupOptions(groupType, this.grouping) as ITableGroupByOptions;
		if (!groupByOptions || !groupByOptions.comparisonValue) {
			return {};
		}
		return common.commonUtils.groupBy(rows, (row) => {
			const queryInstance = new query.Query(groupByOptions.comparisonValue);
			const groupKey = queryInstance.execute({ responseContext: row });
			return groupKey;
		})
	}

	filterRowsByGrouping<TRow extends ITableRow>(rows: TRow[]) {
		const groupType = this.getGroupType(this.grouping);
		const groupOptions = this.getGroupOptions(groupType, this.grouping);
		let workingRows = [...rows];
		if (groupType === 'GroupBy' && groupOptions) {
			// determine if we have config and if the config is able to group anything, otherwise return the rows as is.
			const groups = this.groupRowsBy(workingRows);
			const groupKeys = Object.keys(groups);
			if (groupKeys.length <= 0) {
				return workingRows;
			}
			// recreate rows from the first record in each group. (noting that this group type only has one level of grouping).
			workingRows = [];
			for (let i = 0; i < groupKeys.length; i++) {
				const key = groupKeys[i];
				const group = groups[key];
				const firstRow = structuredClone(group[0]);
				firstRow._groupByChildren = group.filter((row, index) => index > 0);
				if (firstRow._groupByChildren.length <= 0) {
					firstRow._toggleDisabled = true;
				}
				workingRows.push(firstRow);
			}
		}
		return workingRows;
	}

	/**
	 * Update rows to include any group expand column details
	 *
	 * @template TRow
	 * @param options
	 * @returns {TRow[]} rows
	 */
	getRowsWithExpanders<TRow extends ITableRow>(options: { rows: TRow[], grouping?: ITableGroup }) {
		const { rows = [], grouping } = options;

		const newRows = rows.map((row, rowIndex) => {
			const response = { ...row };
			if (!response._rowKey) {
				response._rowKey = common.commonUtils.generateGuid();
			}

			if (row._expanded === undefined) {
				if (grouping && grouping.autoExpandAllGroups !== undefined) {
					response._expanded = grouping.autoExpandAllGroups;
				}

				// If autoExpandFirstGroup is true and forceExpand is false from above, the autoExpand will override the first group to be expanded.
				if (grouping && grouping.autoExpandFirstGroup && rowIndex === 0) {
					response._expanded = true;
				}
			}

			return response;
		});

		return newRows;
	}
}

export default TableLogic;
